import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46013b23-b5fe-4779-b886-ac2171337e18/artifacts/nmw6zqxw_GCI%20Logo-%20Transparent%20-%20Remove_background_from_this_image_make_it_transpa-1771909885908.png";

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: t('footer.home'), path: '/' },
    { name: t('footer.aboutUs'), path: '/about' },
    { name: t('footer.newsBlog'), path: '/news' },
    { name: t('footer.testimonials'), path: '/testimonials' },
    { name: t('footer.resourceLib'), path: '/resources' },
    { name: t('footer.instructors'), path: '/instructors' },
    { name: t('footer.events'), path: '/events' },
    { name: t('nav.eventSpotlight'), path: '/impact-calendar/spotlight' },
    { name: t('footer.contact'), path: '/contact' },
    { name: t('footer.donate'), path: '/donate' },
  ];

  const programLinks = [
    { name: t('footer.workforceDev'), path: '/services/workforce' },
    { name: t('footer.volunteerNet'), path: '/services/volunteer' },
    { name: t('footer.communityCollab'), path: '/services/collaboration' },
    { name: t('footer.healthWellness'), path: '/services/health' },
    { name: t('footer.housingStability'), path: '/services/housing' },
  ];

  const supportLinks = [
    { name: t('footer.careerSupport'), path: '/services/workforce' },
    { name: t('footer.partnerWithUs'), path: '/contact' },
    { name: t('footer.partnerDirectory'), path: '/partners' },
    { name: t('footer.sponsorship'), path: '/sponsorship' },
    { name: t('footer.sponsorPortal'), path: '/sponsor-portal' },
    { name: t('footer.volunteer'), path: '/services/volunteer' },
    { name: t('footer.developerAPI'), path: '/developers' },
  ];

  const legalLinks = [
    { name: t('footer.privacyPolicy'), path: '/privacy' },
    { name: t('footer.termsOfService'), path: '/terms' },
    { name: t('footer.admin'), path: '/admin/login' },
    { name: t('footer.myAccount'), path: '/account' },
    { name: t('footer.myDashboard'), path: '/my-dashboard' },
    { name: t('footer.instructorPortal'), path: '/instructor-portal' },
  ];

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/greatercommunityimpact' },
    { name: 'Twitter', icon: Twitter, url: 'https://twitter.com/gaborimpact' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/greatercommunityimpact' },
    { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/company/greater-community-impact' },
  ];

  return (
    <footer className="bg-brand-blue text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Top row: Logo+Mission | Quick Links | Programs | Support | Legal */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Logo + Mission + Contact */}
          <div className="col-span-2 md:col-span-3 lg:col-span-2">
            <div className="flex items-start gap-4 mb-4">
              <Link to="/" className="shrink-0">
                <img src={LOGO_URL} alt="GCI" className="h-24 w-auto bg-white rounded-lg p-1.5" data-testid="footer-logo" />
              </Link>
              <p className="text-gray-300 text-xs leading-relaxed pt-1">
                {t('footer.mission')}
              </p>
            </div>
            <div className="space-y-1.5 text-xs">
              <a href="mailto:info@greatercommunityimpact.org" className="flex items-center text-gray-300 hover:text-brand-orange transition-colors" data-testid="footer-email">
                <Mail className="h-3.5 w-3.5 mr-2" /> info@greatercommunityimpact.org
              </a>
              <a href="tel:+19122374771" className="flex items-center text-gray-300 hover:text-brand-orange transition-colors" data-testid="footer-phone">
                <Phone className="h-3.5 w-3.5 mr-2" /> (912) 237-4771
              </a>
              <div className="flex items-center text-gray-300">
                <MapPin className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                <span>6205 Abercorn Street, STE 206, Savannah, Georgia 31405</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-white">{t('footer.quickLinks')}</h4>
            <ul className="space-y-0.5">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="block py-1.5 text-xs text-gray-300 hover:text-brand-orange transition-colors min-h-[36px] sm:min-h-0 flex items-center" data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Programs */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-white">{t('footer.ourPrograms')}</h4>
            <ul className="space-y-0.5">
              {programLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="block py-1.5 text-xs text-gray-300 hover:text-brand-orange transition-colors min-h-[36px] sm:min-h-0 flex items-center" data-testid={`footer-program-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-white">{t('footer.support')}</h4>
            <ul className="space-y-0.5">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="block py-1.5 text-xs text-gray-300 hover:text-brand-orange transition-colors min-h-[36px] sm:min-h-0 flex items-center" data-testid={`footer-support-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold mb-3 text-white">{t('footer.legal')}</h4>
            <ul className="space-y-1.5">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-xs text-gray-300 hover:text-brand-orange transition-colors" data-testid={`footer-legal-${link.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social + Copyright */}
        <div className="mt-8 pt-6 border-t border-blue-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Social */}
            <div className="flex items-center gap-2">
              {socialLinks.map((social) => (
                <a key={social.name} href={social.url} className="p-2 bg-blue-800 rounded-full hover:bg-brand-orange transition-colors" aria-label={social.name} data-testid={`footer-social-${social.name.toLowerCase()}`}>
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Copyright */}
          <p className="text-xs text-gray-400 text-center mt-4">
            &copy; {currentYear} {t('footer.copyright')}
            <span className="mx-1">|</span>
            {t('footer.madeWith')} <Heart className="inline h-3 w-3 text-brand-orange" /> {t('footer.forCommunity')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
